/**
 * emailPattern.js — infer a firm's email-address convention from the contacts
 * that already have emails, so contacts missing one can get a suggestion
 * ("Aaron Wei at Ardmore Road → aaron@ardmoreroad.com, because Joey Chai is
 * joey@ardmoreroad.com").
 *
 * buildFirmPatterns(rows) consumes the /api/contacts/email-map rows
 * ([{contact_id, name, email, firm_id}]) and returns a Map keyed by firm_id:
 *   { pattern, domain, count, total, example: { name, email } }
 * where pattern/domain is the most common (pattern, domain) pair among that
 * firm's contacts whose email matches a known name-derived shape, count is how
 * many contacts follow it, and total is how many firm contacts have a
 * corporate email at all. Free-mail domains (gmail etc.) never count — a
 * personal address says nothing about the firm's convention.
 *
 * suggestEmail(name, firmPattern) applies the pattern to a name, or returns
 * null when the name can't be split into first + last.
 */

// Personal-mail domains that never define a firm convention.
const FREE_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com',
  'outlook.com', 'live.com', 'msn.com', 'aol.com', 'icloud.com', 'me.com',
  'mac.com', 'protonmail.com', 'proton.me', 'comcast.net', 'verizon.net',
]);

// Trailing name tokens that aren't a surname.
const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'cfa', 'cpa', 'phd', 'md']);

/** "Aaron Wei" → { first: 'aaron', last: 'wei' }; null if not a two-part name. */
export function splitName(name = '') {
  const tokens = String(name)
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // strip accents
    .toLowerCase()
    .replace(/[^a-z\s]/g, '') // drop punctuation (O'Brien → obrien, Jr. → jr)
    .split(/\s+/)
    .filter((t) => t && !NAME_SUFFIXES.has(t));
  if (tokens.length < 2) return null;
  return { first: tokens[0], last: tokens[tokens.length - 1] };
}

// Local-part shapes, most specific first (order breaks count ties).
const PATTERNS = [
  { key: 'first.last', build: (f, l) => `${f}.${l}` },
  { key: 'first_last', build: (f, l) => `${f}_${l}` },
  { key: 'first-last', build: (f, l) => `${f}-${l}` },
  { key: 'firstlast',  build: (f, l) => `${f}${l}` },
  { key: 'f.last',     build: (f, l) => `${f[0]}.${l}` },
  { key: 'flast',      build: (f, l) => `${f[0]}${l}` },
  { key: 'firstl',     build: (f, l) => `${f}${l[0]}` },
  { key: 'last.first', build: (f, l) => `${l}.${f}` },
  { key: 'lastfirst',  build: (f, l) => `${l}${f}` },
  { key: 'lastf',      build: (f, l) => `${l}${f[0]}` },
  { key: 'first',      build: (f) => f },
  { key: 'last',       build: (_f, l) => l },
];
const PATTERN_BY_KEY = new Map(PATTERNS.map((p) => [p.key, p]));

/** Which known shape (if any) produces this local part from this name. */
function matchPattern(localPart, { first, last }) {
  for (const p of PATTERNS) {
    if (p.build(first, last) === localPart) return p.key;
  }
  return null;
}

/** Map firm_id → dominant email convention. See file header for the shape. */
export function buildFirmPatterns(rows = []) {
  const byFirm = new Map(); // firm_id → { total, combos: Map<'pattern|domain', {count, example}> }
  for (const r of rows) {
    if (!r?.firm_id || !r?.email) continue;
    const email = String(r.email).trim().toLowerCase();
    const at = email.indexOf('@');
    if (at <= 0) continue;
    const domain = email.slice(at + 1);
    if (!domain || FREE_MAIL_DOMAINS.has(domain)) continue;

    let f = byFirm.get(r.firm_id);
    if (!f) { f = { total: 0, combos: new Map() }; byFirm.set(r.firm_id, f); }
    f.total += 1;

    const parts = splitName(r.name);
    if (!parts) continue;
    const pattern = matchPattern(email.slice(0, at), parts);
    if (!pattern) continue;

    const comboKey = `${pattern}|${domain}`;
    const combo = f.combos.get(comboKey);
    if (combo) combo.count += 1;
    else f.combos.set(comboKey, { count: 1, example: { name: r.name, email } });
  }

  const result = new Map();
  for (const [firmId, f] of byFirm) {
    let best = null;
    for (const [comboKey, combo] of f.combos) {
      if (!best || combo.count > best.count) {
        const [pattern, domain] = comboKey.split('|');
        best = { pattern, domain, count: combo.count, example: combo.example };
      }
    }
    if (best) result.set(firmId, { ...best, total: f.total });
  }
  return result;
}

/** Apply a firm's convention to a contact name → 'aaron@ardmoreroad.com' or null. */
export function suggestEmail(name, firmPattern) {
  if (!firmPattern) return null;
  const parts = splitName(name);
  if (!parts) return null;
  const p = PATTERN_BY_KEY.get(firmPattern.pattern);
  if (!p) return null;
  return `${p.build(parts.first, parts.last)}@${firmPattern.domain}`;
}

// When the firm's pattern collides with an existing address (two Aarons at
// Ardmore Road), guess the disambiguator firms typically fall back to, most
// common first.
const FALLBACK_PATTERNS = ['firstl', 'first.last', 'flast', 'firstlast'];

/**
 * Like suggestEmail, but never proposes an address in `takenEmails` (a Set of
 * lowercased existing addresses). When the firm's own pattern is taken, tries
 * the fallback shapes above on the same domain and flags the result:
 *   { email, fallback: false }                            — clean pattern hit
 *   { email, fallback: true, conflictEmail }              — pattern hit was taken
 *   null                                                  — nothing to suggest
 */
export function suggestEmailAvoidingTaken(name, firmPattern, takenEmails) {
  const primary = suggestEmail(name, firmPattern);
  if (!primary) return null;
  if (!takenEmails.has(primary)) return { email: primary, fallback: false };
  const parts = splitName(name);
  for (const key of FALLBACK_PATTERNS) {
    if (key === firmPattern.pattern) continue;
    const candidate = `${PATTERN_BY_KEY.get(key).build(parts.first, parts.last)}@${firmPattern.domain}`;
    if (!takenEmails.has(candidate)) return { email: candidate, fallback: true, conflictEmail: primary };
  }
  return null;
}
